import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Surface } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { useAuth } from '../../providers/AuthProvider';
import { StatusMessage } from '../../components/ui/Feedback';
import { homeFor } from '../../lib/roles';

/** Surfaces that sign in with a one-time email link instead of a password. */
function isMagicLinkSurface(s: Surface): boolean {
  return s === 'tenant' || s === 'contractor';
}

export type LoginStep = 'credentials' | 'mfa-verify' | 'mfa-enroll' | 'recovery-codes';

/** The full controller the pure view renders — the container supplies real handlers; tests stub it. */
export interface LoginController {
  step: LoginStep;
  surface: Surface;
  email: string;
  password: string;
  code: string;
  error: string | null;
  busy: boolean;
  enrollSecret: string | null;
  recoveryCodes: string[] | null;
  /** Admin IAM SSO is configured — show the SSO button on the admin surface. */
  oidcEnabled: boolean;
  /** A tenant magic-link was requested — show the neutral "check your email" confirmation. */
  magicSent: boolean;
  onSurface: (s: Surface) => void;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onCode: (v: string) => void;
  onSubmitCredentials: () => void;
  /** Request a passwordless tenant sign-in link (tenants have no password). */
  onRequestMagicLink: () => void;
  /** Navigate (full page) to the IAM SSO entry point. */
  onSsoLogin: () => void;
  onSubmitMfa: () => void;
  /** Terminal ack for the one-time recovery-codes panel — THIS is what completes sign-in. */
  onContinueFromRecoveryCodes: () => void;
}

/**
 * Pure, presentational login screen (T9.1 — accessible auth surface). Renders inside its own
 * header/main landmarks with a single h1 and labelled controls, so it passes axe on its own. The
 * `admin`/`tenant` surface is a labelled radiogroup; MFA enroll/verify are progressive steps.
 * `recovery-codes` is a TERMINAL step — the one-time codes stay on screen until the operator
 * explicitly acknowledges them (see `onContinueFromRecoveryCodes`), so they never flash and vanish.
 */
export function LoginView({ c }: { c: LoginController }) {
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <header>
          <h1>Sign in to Garage Band LLC</h1>
          <p className="muted">San Luis Obispo property management portal.</p>
        </header>
        <main id="main-content">
          {c.error && <StatusMessage tone="err">{c.error}</StatusMessage>}

          {c.step === 'credentials' && (
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                if (isMagicLinkSurface(c.surface)) c.onRequestMagicLink();
                else c.onSubmitCredentials();
              }}
            >
              <fieldset style={{ border: '1px solid var(--border)', borderRadius: 8 }}>
                <legend>I am signing in as</legend>
                <div className="row" style={{ flexWrap: 'wrap', columnGap: '1.25rem', rowGap: '0.5rem' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}>
                    <input
                      id="surface-tenant"
                      type="radio"
                      name="surface"
                      style={{ width: 'auto' }}
                      checked={c.surface === 'tenant'}
                      onChange={() => c.onSurface('tenant')}
                    />
                    <label htmlFor="surface-tenant" style={{ margin: 0 }}>
                      Tenant
                    </label>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}>
                    <input
                      id="surface-admin"
                      type="radio"
                      name="surface"
                      style={{ width: 'auto' }}
                      checked={c.surface === 'admin'}
                      onChange={() => c.onSurface('admin')}
                    />
                    <label htmlFor="surface-admin" style={{ margin: 0 }}>
                      Owner or staff
                    </label>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}>
                    <input
                      id="surface-contractor"
                      type="radio"
                      name="surface"
                      style={{ width: 'auto' }}
                      checked={c.surface === 'contractor'}
                      onChange={() => c.onSurface('contractor')}
                    />
                    <label htmlFor="surface-contractor" style={{ margin: 0 }}>
                      Contractor
                    </label>
                  </span>
                </div>
              </fieldset>

              {c.surface === 'admin' && c.oidcEnabled && (
                <section className="stack" aria-label="Single sign-on">
                  <button type="button" className="primary" onClick={c.onSsoLogin} disabled={c.busy}>
                    Sign in with IAM (SSO)
                  </button>
                  <p className="muted" style={{ textAlign: 'center', margin: 0 }}>
                    or sign in with your password (break-glass)
                  </p>
                </section>
              )}

              <div>
                <label htmlFor="login-email">Email address</label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={c.email}
                  onChange={(e) => c.onEmail(e.target.value)}
                />
              </div>

              {isMagicLinkSurface(c.surface) ? (
                // Tenants + contractors sign in passwordlessly — request a one-time link (anti-
                // enumeration: the confirmation is identical whether or not the address exists).
                c.magicSent ? (
                  <StatusMessage tone="ok">
                    If that email has a {c.surface === 'contractor' ? 'contractor' : 'tenant'} account,
                    a sign-in link is on its way. Check your inbox — the link expires in 15 minutes.
                  </StatusMessage>
                ) : (
                  <>
                    <p className="field-hint">
                      {c.surface === 'contractor' ? 'Contractors' : 'Tenants'} sign in with a one-time
                      link — no password needed.
                    </p>
                    <button type="submit" disabled={c.busy}>
                      {c.busy ? 'Sending…' : 'Email me a sign-in link'}
                    </button>
                  </>
                )
              ) : (
                <>
                  <div>
                    <label htmlFor="login-password">Password</label>
                    <input
                      id="login-password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={c.password}
                      onChange={(e) => c.onPassword(e.target.value)}
                    />
                  </div>
                  <button type="submit" disabled={c.busy}>
                    {c.busy ? 'Signing in…' : 'Sign in'}
                  </button>
                </>
              )}
            </form>
          )}

          {(c.step === 'mfa-verify' || c.step === 'mfa-enroll') && (
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                c.onSubmitMfa();
              }}
            >
              {c.step === 'mfa-enroll' && (
                <>
                  <h2>Set up two-factor authentication</h2>
                  <p>
                    Add this secret to your authenticator app, then enter the 6-digit code it
                    generates.
                  </p>
                  {c.enrollSecret && (
                    <p>
                      <span className="muted">Secret: </span>
                      <code>{c.enrollSecret}</code>
                    </p>
                  )}
                </>
              )}
              {c.step === 'mfa-verify' && <h2>Enter your authentication code</h2>}

              <div>
                <label htmlFor="mfa-code">Authentication code</label>
                <input
                  id="mfa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={c.code}
                  onChange={(e) => c.onCode(e.target.value)}
                />
              </div>
              <button type="submit" disabled={c.busy}>
                {c.busy ? 'Verifying…' : 'Verify'}
              </button>
            </form>
          )}

          {c.step === 'recovery-codes' && (
            <section className="stack notice-callout" aria-labelledby="recovery-codes-heading">
              <h2 id="recovery-codes-heading" style={{ marginTop: 0 }}>
                Save your recovery codes
              </h2>
              <p>Store these somewhere safe — each can be used once if you lose your device.</p>
              <ul>
                {(c.recoveryCodes ?? []).map((rc) => (
                  <li key={rc}>
                    <code>{rc}</code>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={c.onContinueFromRecoveryCodes}>
                I have saved my recovery codes
              </button>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

/** Container: drives the login → optional MFA flow, then routes to the correct shell. */
export function Login() {
  const navigate = useNavigate();
  const { setCsrfToken, refresh } = useAuth();

  const [step, setStep] = useState<LoginStep>('credentials');
  const [surface, setSurface] = useState<Surface>('tenant');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [enrollSecret, setEnrollSecret] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [oidcEnabled, setOidcEnabled] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  // Is admin IAM SSO configured? Public, pre-session read — a failure just hides the button.
  useEffect(() => {
    let live = true;
    api.auth
      .config()
      .then((cfg) => {
        if (live) setOidcEnabled(cfg.oidcEnabled);
      })
      .catch(() => {
        /* SSO stays hidden on any error */
      });
    return () => {
      live = false;
    };
  }, []);

  /** Full-page navigation to the IAM SSO entry point (302 → IAM; carries the state cookie back). */
  const onSsoLogin = () => {
    window.location.assign(api.auth.oidcLoginUrl());
  };

  /** Switch surface, clearing any in-flight magic-link confirmation / error. */
  const onSurface = (s: Surface) => {
    setSurface(s);
    setMagicSent(false);
    setError(null);
  };

  /** Request a passwordless tenant sign-in link. Always "succeeds" (anti-enumeration). */
  const onRequestMagicLink = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.auth.magicLink({ email, surface: isMagicLinkSurface(surface) ? surface : 'tenant' });
      setMagicSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const goHome = async (role: string) => {
    await refresh();
    navigate(homeFor(role));
  };

  const onSubmitCredentials = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.auth.login({ email, password, surface });
      setCsrfToken(res.csrfToken);
      if (res.principal.role === 'tenant' || res.principal.role === 'contractor') {
        await goHome(res.principal.role);
        return;
      }
      if (res.mustEnrollMfa) {
        const enroll = await api.auth.mfaEnroll();
        setEnrollSecret(enroll.secret);
        setStep('mfa-enroll');
      } else if (res.mfaRequired) {
        setStep('mfa-verify');
      } else {
        await goHome(res.principal.role);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onSubmitMfa = async () => {
    setBusy(true);
    setError(null);
    try {
      if (step === 'mfa-enroll') {
        const res = await api.auth.mfaConfirm({ code });
        setCsrfToken(res.csrfToken);
        setRecoveryCodes(res.recoveryCodes);
        // The session is now fully authed, but do NOT refresh()/navigate yet — the one-time
        // recovery codes must stay on screen as a TERMINAL step until the operator explicitly
        // acknowledges them (onContinueFromRecoveryCodes), or they'd flash and vanish.
        setStep('recovery-codes');
      } else {
        const res = await api.auth.mfaVerify({ code });
        setCsrfToken(res.csrfToken);
        await goHome(res.principal.role);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Recovery codes only ever occur on the MFA-enroll path (owner/staff) — the tenant flow (no MFA)
  // never reaches this step, so it's always safe to land in the admin shell.
  const onContinueFromRecoveryCodes = async () => {
    await refresh();
    navigate('/admin');
  };

  return (
    <LoginView
      c={{
        step,
        surface,
        email,
        password,
        code,
        error,
        busy,
        enrollSecret,
        recoveryCodes,
        oidcEnabled,
        magicSent,
        onSurface,
        onEmail: setEmail,
        onPassword: setPassword,
        onCode: setCode,
        onSubmitCredentials,
        onRequestMagicLink,
        onSsoLogin,
        onSubmitMfa,
        onContinueFromRecoveryCodes,
      }}
    />
  );
}
